export const MAIL_SEARCH_OPERATOR_FIELDS = [
  'from',
  'to',
  'participant',
  'subject',
  'is',
  'has',
  'in',
  'account',
  'before',
  'after',
] as const;

export type MailSearchOperatorField = (typeof MAIL_SEARCH_OPERATOR_FIELDS)[number];

export interface MailSearchTextTerm {
  value: string;
  quoted: boolean;
  prefix: boolean;
}

export interface MailSearchOperator {
  field: MailSearchOperatorField;
  value: string;
  quoted: boolean;
}

/**
 * Adjacent clauses are ANDed. Terms within one clause are explicit OR
 * alternatives, so `invoice OR receipt canada` means
 * `(invoice OR receipt) AND canada`.
 */
export interface MailSearchSyntax {
  textClauses: MailSearchTextTerm[][];
  operators: MailSearchOperator[];
}

interface RawToken {
  raw: string;
  start: number;
  end: number;
}

const OPERATOR_FIELDS = new Set<string>(MAIL_SEARCH_OPERATOR_FIELDS);

export class MailSearchSyntaxError extends Error {}

export function parseMailSearchSyntax(input: string): MailSearchSyntax {
  const textClauses: MailSearchTextTerm[][] = [];
  const operators: MailSearchOperator[] = [];
  let expectingAlternative = false;
  let previousWasText = false;

  for (const token of tokenizeMailSearch(input.trim())) {
    if (token.raw.toUpperCase() === 'OR') {
      if (!previousWasText || expectingAlternative) {
        throw new MailSearchSyntaxError('OR must appear between two search words or phrases');
      }
      expectingAlternative = true;
      previousWasText = false;
      continue;
    }

    const separator = token.raw.indexOf(':');
    const candidate = separator > 0 ? token.raw.slice(0, separator).toLowerCase() : '';
    if (OPERATOR_FIELDS.has(candidate)) {
      if (expectingAlternative) {
        throw new MailSearchSyntaxError('OR can join words or phrases, not search operators');
      }
      const parsed = parseSearchValue(token.raw.slice(separator + 1));
      if (!parsed.value) {
        throw new MailSearchSyntaxError(`missing value for ${candidate}:`);
      }
      operators.push({
        field: candidate as MailSearchOperatorField,
        value: parsed.value,
        quoted: parsed.quoted,
      });
      previousWasText = false;
      continue;
    }

    const term = parseTextTerm(token.raw);
    if (!term.value) continue;
    if (expectingAlternative) {
      textClauses.at(-1)!.push(term);
      expectingAlternative = false;
    } else {
      textClauses.push([term]);
    }
    previousWasText = true;
  }

  if (expectingAlternative) {
    throw new MailSearchSyntaxError('OR must be followed by a search word or phrase');
  }
  return { textClauses, operators };
}

/** Add explicit prefix matching to every ordinary unquoted text term. */
export function broadenMailSearchQuery(input: string): string {
  const tokens = tokenizeMailSearch(input.trim());
  // Validate first so this transformer never turns an invalid query into a
  // different valid one.
  parseMailSearchSyntax(input);
  return tokens
    .map((token) => {
      const separator = token.raw.indexOf(':');
      const candidate = separator > 0 ? token.raw.slice(0, separator).toLowerCase() : '';
      if (
        token.raw.toUpperCase() === 'OR' ||
        OPERATOR_FIELDS.has(candidate) ||
        token.raw.startsWith('"') ||
        token.raw.endsWith('*')
      ) {
        return token.raw;
      }
      return `${token.raw}*`;
    })
    .join(' ');
}

/** Remove explicit prefix markers while preserving phrases and operators. */
export function narrowMailSearchQuery(input: string): string {
  const tokens = tokenizeMailSearch(input.trim());
  parseMailSearchSyntax(input);
  return tokens
    .map((token) => {
      const separator = token.raw.indexOf(':');
      const candidate = separator > 0 ? token.raw.slice(0, separator).toLowerCase() : '';
      return !OPERATOR_FIELDS.has(candidate) && token.raw.endsWith('*')
        ? token.raw.slice(0, -1)
        : token.raw;
    })
    .join(' ');
}

export function hasPrefixMailSearchTerms(input: string): boolean {
  return parseMailSearchSyntax(input).textClauses.some((clause) =>
    clause.some((term) => term.prefix),
  );
}

/**
 * Match FTS-like exact-token, phrase, prefix, AND, and OR semantics against
 * one or more local strings. This keeps encrypted-offline metadata search
 * predictable without exposing the vault to server code.
 */
export function matchesMailSearchText(
  sources: readonly string[],
  clauses: readonly (readonly MailSearchTextTerm[])[],
): boolean {
  if (clauses.length === 0) return true;
  const sourceTokens = sources.map(tokenizeComparableText);
  return clauses.every((clause) =>
    clause.some((term) => {
      const expected = tokenizeComparableText(term.value);
      if (expected.length === 0) return false;
      return sourceTokens.some((actual) => containsTokenSequence(actual, expected, term.prefix));
    }),
  );
}

export function mailSearchTextTerms(input: string): MailSearchTextTerm[] {
  return parseMailSearchSyntax(input).textClauses.flat();
}

function tokenizeMailSearch(input: string): RawToken[] {
  const tokens: RawToken[] = [];
  let current = '';
  let start = 0;
  let quoted = false;
  let escaped = false;
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index]!;
    if (current.length === 0) start = index;
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }
    if (char === '\\' && quoted) {
      current += char;
      escaped = true;
      continue;
    }
    if (char === '"') {
      quoted = !quoted;
      current += char;
      continue;
    }
    if (/\s/u.test(char) && !quoted) {
      if (current) tokens.push({ raw: current, start, end: index });
      current = '';
      continue;
    }
    current += char;
  }
  if (quoted) throw new MailSearchSyntaxError('unterminated quote');
  if (current) tokens.push({ raw: current, start, end: input.length });
  return tokens;
}

function parseTextTerm(raw: string): MailSearchTextTerm {
  const parsed = parseSearchValue(raw);
  if (parsed.quoted) {
    if (parsed.value.includes('*')) {
      throw new MailSearchSyntaxError(
        'prefix * is supported only at the end of an unquoted search word',
      );
    }
    return { ...parsed, prefix: false };
  }
  const stars = [...parsed.value.matchAll(/\*/gu)];
  if (stars.length === 0) return { ...parsed, prefix: false };
  if (stars.length !== 1 || !parsed.value.endsWith('*') || parsed.value.length === 1) {
    throw new MailSearchSyntaxError(
      'prefix * is supported only once, at the end of a search word',
    );
  }
  return { value: parsed.value.slice(0, -1), quoted: false, prefix: true };
}

function parseSearchValue(raw: string): { value: string; quoted: boolean } {
  const quoted = raw.length >= 2 && raw.startsWith('"') && raw.endsWith('"');
  const value = quoted
    ? raw.slice(1, -1).replace(/\\"/gu, '"').replace(/\\\\/gu, '\\')
    : raw;
  return { value: value.trim(), quoted };
}

function tokenizeComparableText(value: string): string[] {
  return (
    value
      .normalize('NFD')
      .replace(/\p{M}+/gu, '')
      .toLowerCase()
      .match(/[\p{L}\p{N}_]+/gu) ?? []
  );
}

function containsTokenSequence(
  actual: readonly string[],
  expected: readonly string[],
  prefix: boolean,
): boolean {
  if (expected.length > actual.length) return false;
  for (let start = 0; start <= actual.length - expected.length; start += 1) {
    let matches = true;
    for (let offset = 0; offset < expected.length; offset += 1) {
      const isLast = offset === expected.length - 1;
      if (
        (prefix && isLast
          ? !actual[start + offset]!.startsWith(expected[offset]!)
          : actual[start + offset] !== expected[offset])
      ) {
        matches = false;
        break;
      }
    }
    if (matches) return true;
  }
  return false;
}
